import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpService } from '@nebula-shared/data-access';
import { finalize } from 'rxjs/operators';

/** Estados de la pantalla pública de activación. */
type ActivarEstado = 'validando' | 'invalido' | 'formulario' | 'enviando' | 'listo';

/**
 * Página PÚBLICA de activación de usuario Nebula. Recibe el dato cifrado del
 * enlace ({@code ?d=<blob>}), valida su vigencia contra nebula-masters y, si es
 * válido, presenta el formulario para definir la contraseña (política
 * internacional). Al confirmar, activa la cuenta y redirige al login.
 */
@Component({
  selector: 'nebula-activar',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './activar.component.html',
  styleUrls: ['./activar.component.scss'],
})
export class ActivarComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly http = inject(HttpService);

  /** Endpoints públicos de activación en nebula-masters. */
  private static readonly VALIDAR = '/nebula-masters/api/v1/usuario/activacion/validar';
  private static readonly CONFIRMAR = '/nebula-masters/api/v1/usuario/activacion/confirmar';

  /** Dato cifrado del enlace (parámetro {@code d}). */
  private data = '';

  /** Estado de la pantalla. */
  readonly estado = signal<ActivarEstado>('validando');

  /** Mensaje de error a mostrar (si aplica). */
  readonly error = signal<string>('');

  /** Formulario de definición de contraseña. */
  readonly form: FormGroup = this.fb.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/),
        ],
      ],
      confirmar: ['', [Validators.required]],
    },
    { validators: ActivarComponent.contrasenasCoinciden },
  );

  /** Validador de coincidencia de contraseñas. */
  private static contrasenasCoinciden(group: AbstractControl): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const conf = group.get('confirmar')?.value;
    return pass && conf && pass !== conf ? { noCoinciden: true } : null;
  }

  ngOnInit(): void {
    this.data = this.route.snapshot.queryParamMap.get('d') ?? '';
    if (!this.data) {
      this.estado.set('invalido');
      return;
    }
    this.http.get<boolean>(ActivarComponent.VALIDAR, { d: this.data }).subscribe({
      next: (res) => this.estado.set(res?.response?.content ? 'formulario' : 'invalido'),
      error: () => this.estado.set('invalido'),
    });
  }

  /** Confirma la activación: envía la contraseña y, al terminar, redirige al login. */
  confirmar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.error.set('');
    this.estado.set('enviando');
    this.http
      .post<void>(ActivarComponent.CONFIRMAR, {
        data: this.data,
        password: this.form.value.password,
      })
      .pipe(finalize(() => this.form.enable()))
      .subscribe({
        next: () => {
          this.estado.set('listo');
          setTimeout(() => this.router.navigate(['/login']), 2500);
        },
        error: (e) => {
          this.estado.set('formulario');
          this.error.set(
            e?.error?.message ??
              'No se pudo activar la cuenta. Verifica la contraseña o solicita un nuevo enlace.',
          );
        },
      });
  }

  /** Ir al login manualmente. */
  irAlLogin(): void {
    this.router.navigate(['/login']);
  }
}
